/**
 * 10NEWTONS portal design tokens — the single source of truth for the
 * "Version D · Clinical Bold" redesign (team pick, 2026-07-16).
 *
 * Two deliberately separate colour systems:
 *
 *   1. BRAND / SURFACE (the teal ramp) — chrome only: backgrounds,
 *      the nav rail, cards, accents. Never used to mean "good/bad".
 *
 *   2. FUNCTIONAL STATUS (red / amber / green) — meaning only, and kept
 *      visually OUTSIDE the brand teal so a "good" green never blurs
 *      into the green-teal brand. Wired into MUI as success/warning/
 *      error so `theme.palette.success` etc. carry the status colours.
 *
 * Status is always encoded three ways — icon + shape + text + colour —
 * so it survives a colourblind read (see StatusChip). The teal-heavy
 * palette makes green-vs-teal discrimination harder, which is exactly
 * why colour alone is never enough.
 */

/** Brand + surface ramp (light mode). Deeper, "richer without colourful"
 *  refresh (2026-07-18): a deep-teal chrome (#0F5C68) for the nav rail +
 *  table headers, a vivid teal primary (#1A8C8E) for interactive elements,
 *  and a very light workspace so white cards read as the surface. Body text
 *  stays a neutral slate (`text`) so the app doesn't go all-teal. */
export const brand = {
  // Clean, near-white workspace with a whisper of teal so white cards +
  // the hairline/shadow still separate it (Stanford-inspired background).
  bg: '#F6FAFA', // app background
  tint: '#E4F1F0', // subtle surface / hover / selected tint
  accent: '#5FD3CE', // bright interactive accent — pops on the deep rail
  main: '#1A8C8E', // brand primary (buttons, links, active accents)
  deep: '#0F5C68', // deep teal — pressed + the nav rail / header chrome
  ink: '#0F5C68', // CHROME ink — nav rail + table-header band (deep teal)
  text: '#22323A', // BODY text — a neutral slate, kept off-teal for reading
  inkSoft: '#33505b',
  muted: '#5c7a83', // secondary text
  hair: '#DBE8E6', // hairline borders on the lighter background
} as const;

/** Brand ramp tuned for dark surfaces (desaturated so it reads as accent). */
export const brandDark = {
  bg: '#0f1418',
  paper: '#1a1f24',
  tint: 'rgba(116,211,206,0.12)',
  accent: '#74D3CE',
  main: '#3aa494',
  deep: '#2b8375',
  ink: '#141b21', // nav rail on dark
  inkSoft: '#22303a',
  muted: '#8aa6ad',
  hair: '#26313a',
} as const;

/** Functional status — light mode. `good/warn/bad` = success/warning/error. */
export const status = {
  good: '#2E9D57',
  goodBg: '#E6F4EA',
  // Two extra "good" tiers so improving performance feels rewarding, not
  // just pass/fail: good → great (deep green) → elite (blue).
  great: '#1E7A45', // deep green — at/above expert
  elite: '#2E6BC9', // blue — beyond the reference cohorts
  warn: '#F39C12', // brighter, warmer amber
  warnBg: '#FCEFD9',
  bad: '#D64545',
  badBg: '#FBE7E7',
  info: '#2E6BC9',
} as const;

/** Functional status — dark mode (lifted for contrast on dark paper). */
export const statusDark = {
  good: '#4FBE72',
  goodBg: 'rgba(79,190,114,0.16)',
  warn: '#ECB646',
  warnBg: 'rgba(236,182,70,0.16)',
  bad: '#E56E6E',
  badBg: 'rgba(229,110,110,0.16)',
  info: '#5BA0DA',
} as const;

/** Corner radii. Cards are the roundest surface in Version D. */
export const radii = {
  base: 12,
  card: 16,
  pill: 999,
} as const;

/** Semantic status keys used across the canonical components. */
export type StatusKey = 'good' | 'warn' | 'bad' | 'neutral';

/** Maps a semantic status to the MUI palette colour key it renders with. */
export const STATUS_TO_PALETTE: Record<
  Exclude<StatusKey, 'neutral'>,
  'success' | 'warning' | 'error'
> = {
  good: 'success',
  warn: 'warning',
  bad: 'error',
};
